import { Box, TextField, Typography } from "@mui/material"
import type { FC } from "react"
import { useFormContext } from "react-hook-form"

import { useInventoryDestinationFields } from "../../../../hooks"
import type { SplitPalletsAdjustmentFormValues } from "../../schemas"

import { FormAutocomplete } from "@/components/UiKit/FormControls/FormAutocomplete"
import { FormSelect } from "@/components/UiKit/FormControls/FormSelect"
import { FormTextField } from "@/components/UiKit/FormControls/FormTextField"
import { DOCUMENT_UPLOAD_MIME_TYPES, DOCUMENT_UPLOAD_TYPES_LABEL } from "@/components/upload/constants"
import FileInput from "@/components/upload/FileInput"
import { BIN_SIZE_OPTIONS } from "@/constants/warehouse"
import type { WarehouseArea } from "@/graphql/codegen/graphql"
import { formatWarehouseLabel } from "@/utils/warehouse"

interface SplitPalletsAdjustmentFormDetailsProps {
  warehouseArea?: WarehouseArea | null
}

export const SplitPalletsAdjustmentFormDetails: FC<SplitPalletsAdjustmentFormDetailsProps> = ({ warehouseArea }) => {
  const warehouseLabel = warehouseArea ? formatWarehouseLabel(warehouseArea) : ""
  const { control, watch, setValue } = useFormContext<SplitPalletsAdjustmentFormValues>()

  const {
    fieldPaths,
    aisleCode,
    sectionCode,
    shouldShowShelvingFields,
    holdAreaOptions,
    aisleOptions,
    shelfOptions,
    binOptions,
    isFetching,
    handleHoldAreaChange,
    handleBinSizeChange,
    handleAisleCodeChange,
    handleSectionCodeChange,
    handleBinNumberChange,
  } = useInventoryDestinationFields<SplitPalletsAdjustmentFormValues>({
    watch,
    setValue,
    warehouseArea,
    autoSelectFirstAvailable: false,
  })

  return (
    <Box className='flex flex-col overflow-hidden rounded-lg border border-gray-400'>
      <Typography variant='h6' className='p-4 font-bold leading-10'>
        3. Adjustment Details
      </Typography>
      <Box className='flex min-h-0 flex-1 flex-col gap-y-6 overflow-y-auto px-4 pb-4'>
        <Typography variant='subtitle2' className='font-bold'>
          Destination Details
        </Typography>
        <TextField
          label='Warehouse'
          value={warehouseLabel}
          disabled
          data-testid='inventory.split-pallets-adjustment-form-details.warehouse-field'
        />
        <FormSelect
          control={control}
          name={fieldPaths.holdArea}
          label='Area'
          required
          placeholder='Select one'
          options={holdAreaOptions}
          onChange={handleHoldAreaChange}
          data-testid='inventory.split-pallets-adjustment-form-details.area-field'
        />
        {shouldShowShelvingFields && (
          <>
            <Box className='flex gap-6'>
              <FormAutocomplete
                control={control}
                name={fieldPaths.binSize}
                label='Bin Size'
                placeholder='Select one'
                options={BIN_SIZE_OPTIONS}
                onChange={handleBinSizeChange}
                data-testid='inventory.split-pallets-adjustment-form-details.bin-size-field'
              />
              <FormAutocomplete
                control={control}
                name={fieldPaths.aisleCode}
                label='Aisle'
                required
                placeholder='Select one'
                options={aisleOptions}
                disabled={isFetching}
                noOptionsText='No aisles'
                onChange={handleAisleCodeChange}
                data-testid='inventory.split-pallets-adjustment-form-details.aisle-field'
              />
            </Box>
            <Box className='flex gap-6'>
              <FormAutocomplete
                control={control}
                name={fieldPaths.sectionCode}
                label='Shelf'
                required
                placeholder='Select one'
                options={shelfOptions}
                disabled={!aisleCode || isFetching}
                noOptionsText='No shelves'
                onChange={handleSectionCodeChange}
                data-testid='inventory.split-pallets-adjustment-form-details.shelf-field'
              />
              <FormAutocomplete
                control={control}
                name={fieldPaths.binNumber}
                label='Bin'
                required
                placeholder='Select one'
                options={binOptions}
                disabled={!sectionCode || isFetching}
                noOptionsText='No bins'
                onChange={handleBinNumberChange}
                data-testid='inventory.split-pallets-adjustment-form-details.bin-field'
              />
            </Box>
          </>
        )}
        <FormTextField
          control={control}
          name='comment'
          label='Comment'
          placeholder='Enter comment'
          multiline
          rows={4}
          data-testid='inventory.split-pallets-adjustment-form-details.comment-field'
        />
        <FileInput<SplitPalletsAdjustmentFormValues>
          name='document'
          allowedMimeTypes={DOCUMENT_UPLOAD_MIME_TYPES}
          allowedTypesLabel={DOCUMENT_UPLOAD_TYPES_LABEL}
          data-testid='inventory.split-pallets-adjustment-form-details.document-field'
        />
      </Box>
    </Box>
  )
}
